import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import { getServices } from '../firebase.js';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

const Browse = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('streamers');
  const [streams, setStreams] = useState([]);
  const [featuredStreamers, setFeaturedStreamers] = useState([]);
  const [popularGames, setPopularGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { db } = getServices();
  
  useEffect(() => {
    // Fetch data from Firebase
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch live streams
        const streamsQuery = query(
          collection(db, 'streams'),
          where('status', '==', 'live'),
          orderBy('viewerCount', 'desc'),
          limit(10)
        );
        
        const streamsSnapshot = await getDocs(streamsQuery);
        const fetchedStreams = [];
        
        for (const doc of streamsSnapshot.docs) {
          const streamData = doc.data();
          
          // Fetch streamer info
          try {
            const streamerDoc = await getDocs(
              query(collection(db, 'users'), where('uid', '==', streamData.userId), limit(1))
            );
            
            if (!streamerDoc.empty) {
              const streamerData = streamerDoc.docs[0].data();
              
              fetchedStreams.push({
                id: doc.id,
                title: streamData.title || 'Untitled Stream',
                streamer: streamerData.displayName || streamerData.username || 'Unknown Streamer',
                game: streamData.game || 'Unknown Game',
                viewers: streamData.viewerCount || 0,
                thumbnailUrl: streamData.thumbnailUrl || `https://placehold.co/500x300/0f172a/1e3a8a/webp?text=${streamData.game}+Stream`,
                isLive: true,
                isFeatured: streamData.featured || false,
                tags: streamData.tags || [],
                startedAt: streamData.startedAt ? new Date(streamData.startedAt.toDate()).toLocaleTimeString() : 'Recently'
              });
            }
          } catch (error) {
            console.error('Error fetching streamer data:', error);
          }
        }
        
        setStreams(fetchedStreams);
        
        // Fetch featured streamers
        const streamersQuery = query(
          collection(db, 'users'),
          where('isStreamer', '==', true),
          orderBy('followerCount', 'desc'),
          limit(6)
        );
        
        const streamersSnapshot = await getDocs(streamersQuery);
        const fetchedStreamers = streamersSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            username: data.username || 'user',
            displayName: data.displayName || data.username || 'Streamer',
            game: data.currentGame || 'Not streaming',
            viewerCount: data.currentViewers || 0,
            thumbnailUrl: data.profileImage || 'https://via.placeholder.com/320x180',
            isLive: data.isLive || false
          };
        });
        
        setFeaturedStreamers(fetchedStreamers);
        
        // Fetch popular games
        const gamesQuery = query(
          collection(db, 'games'),
          orderBy('viewerCount', 'desc'),
          limit(8)
        );
        
        const gamesSnapshot = await getDocs(gamesQuery);
        const fetchedGames = gamesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || 'Unknown Game',
            viewerCount: data.viewerCount || 0,
            thumbnailUrl: data.boxArtUrl || 'https://via.placeholder.com/200x270',
            channelCount: data.channelCount || 0
          };
        });
        
        setPopularGames(fetchedGames);
        
      } catch (error) {
        console.error('Error fetching browse data:', error);
        setError('Failed to load content. Please try again later.');
        
        // Fallback to empty arrays if there's an error
        setStreams([]);
        setFeaturedStreamers([]);
        setPopularGames([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [db]);

  // Filter streamers based on search query
  const filteredStreamers = featuredStreamers.filter(streamer => 
    streamer.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    streamer.game?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter games based on search query
  const filteredGames = popularGames.filter(game => 
    game.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 text-harmony-white">Browse</h1>
        <p className="text-harmony-white/70">Discover streams, games, and channels</p>
      </div>

      {/* Search and filters */}
      <div className="flex mb-6">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-harmony-white/50" />
          <input
            type="text"
            placeholder="Search streamers, games..."
            className="w-full bg-harmony-blue text-harmony-white rounded-lg py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-harmony-orange border border-harmony-blue/60"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-harmony-blue mb-6">
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'streamers'
              ? 'text-harmony-orange border-b-2 border-harmony-orange'
              : 'text-harmony-white/70 hover:text-harmony-white'
          }`}
          onClick={() => setActiveTab('streamers')}
        >
          Live Streamers
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'games'
              ? 'text-harmony-orange border-b-2 border-harmony-orange'
              : 'text-harmony-white/70 hover:text-harmony-white'
          }`}
          onClick={() => setActiveTab('games')}
        >
          Games
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-6">
          <p className="text-red-300">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-red-500/30 hover:bg-red-500/50 rounded-lg text-red-100 text-sm transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="py-12">
          <div className="flex justify-center mb-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-harmony-orange"></div>
          </div>
          <p className="text-center text-harmony-white/70">Loading content...</p>
        </div>
      ) : (
        <>
          {/* Content */}
          {activeTab === 'streamers' && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-harmony-white">Featured Streamers</h2>
              {filteredStreamers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredStreamers.map((streamer) => (
                    <motion.div
                      key={streamer.id}
                      whileHover={{ y: -5 }}
                      className="bg-harmony-blue-dark rounded-lg overflow-hidden"
                    >
                      <Link to={`/stream/${streamer.username}`}>
                        <div className="relative">
                          <img 
                            src={streamer.thumbnailUrl} 
                            alt={streamer.displayName} 
                            className="w-full h-40 object-cover"
                          />
                          {streamer.isLive && (
                            <div className="absolute top-2 left-2 bg-red-600 text-harmony-white text-xs px-2 py-1 rounded-md flex items-center">
                              <span className="w-2 h-2 bg-harmony-white rounded-full mr-1"></span>
                              LIVE
                            </div>
                          )}
                          <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-harmony-white text-sm px-2 py-1 rounded">
                            {streamer.viewerCount.toLocaleString()} viewers
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="font-medium text-harmony-white">{streamer.displayName}</h3>
                          <p className="text-harmony-white/70 text-sm">{streamer.game}</p>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-harmony-blue-dark rounded-lg">
                  <p className="text-harmony-white/70">No streamers found{searchQuery ? ` matching "${searchQuery}"` : ''}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'games' && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-harmony-white">Popular Games</h2>
              {filteredGames.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {filteredGames.map((game) => (
                    <motion.div
                      key={game.id}
                      whileHover={{ y: -5 }}
                      className="bg-harmony-blue-dark rounded-lg overflow-hidden"
                    >
                      <Link to={`/category/${game.id}`}>
                        <img 
                          src={game.thumbnailUrl} 
                          alt={game.name} 
                          className="w-full h-60 object-cover"
                        />
                        <div className="p-3">
                          <h3 className="font-medium text-harmony-white text-sm truncate">{game.name}</h3>
                          <p className="text-harmony-white/70 text-xs">{game.viewerCount.toLocaleString()} viewers</p>
                          <p className="text-harmony-white/50 text-xs">{game.channelCount} channels</p>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-harmony-blue-dark rounded-lg">
                  <p className="text-harmony-white/70">No games found{searchQuery ? ` matching "${searchQuery}"` : ''}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Browse; 